import React, { useEffect, useState } from "react";
import {
  ModalField,
  ModalForm,
  ModalInput,
  ModalReadout,
} from "../../../design-system/patterns/modal-form";
import { paymentModule } from "../../../api/modules/payment.module";
import { formatCurrency } from "../../../utils/common";

/**
 * Request a payout. One of the nine modal forms.
 *
 * The library has no payout concept at all, so this is designed from the app's
 * real data and nothing else: a balance, an amount, and what is left. There is
 * no fee line, no arrival estimate and no destination account name, because the
 * endpoint returns none of those and inventing them would be inventing a promise.
 *
 * PRIVACY. The amount and the balance stay in component state. They are never
 * logged, never put in a query string and never reflected into the dialog title.
 * The previous version's `onSuccess` handler logged the whole payout response to
 * the console; that is gone.
 *
 * Amounts are RANDS via `formatCurrency`, which prefixes `R`.
 */
const PayoutRequestModal = ({ isOpen, onClose, currentBalance, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const remainingBalance = currentBalance - (parseFloat(amount) || 0);

  useEffect(() => {
    if (isOpen) {
      setAmount("");
      setError("");
    }
  }, [isOpen]);

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setAmount(value);
      setError("");
    }
  };

  const handleSubmit = async () => {
    const amountValue = parseFloat(amount);

    if (!amount || amountValue <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    if (amountValue > currentBalance) {
      setError("Amount cannot exceed current balance");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await paymentModule.requestPayout({
        amount: amountValue,
      });

      if (response.success) {
        onSuccess?.(response.data);
        onClose();
      } else {
        setError(response.message || "Failed to submit payout request");
      }
    } catch (submitError) {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalForm
      open={isOpen}
      title="Request payout"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitting={isLoading}
      submitLabel="Request payout"
      submittingLabel="Requesting"
      submitDisabled={
        !amount || parseFloat(amount) <= 0 || parseFloat(amount) > currentBalance
      }
    >
      <ModalReadout label="Current balance" value={formatCurrency(currentBalance)} />

      <ModalField label="Amount to request" error={error}>
        <ModalInput
          value={amount}
          onChange={handleAmountChange}
          placeholder="0.00"
          inputMode="decimal"
          autoComplete="off"
          disabled={isLoading}
        />
      </ModalField>

      {amount && !error ? (
        <ModalReadout
          label="Remaining balance"
          value={formatCurrency(remainingBalance)}
          tone={remainingBalance < 0 ? "error" : "default"}
        />
      ) : null}
    </ModalForm>
  );
};

export default PayoutRequestModal;
